import { useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Button, Skeleton, Stack, Typography } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { WasteEpisodicEventStatus } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { Link as RouterLink } from "react-router-dom";

import { formatContainerDate } from "../../Containers/containerDisplay";
import { FACILITY_EPISODIC_EVENTS_QUERY } from "../../EpisodicEvents/api";
import { useWasteFacilityState } from "./WasteFacilityContext";

/**
 * Episodic events summary for the waste facility dashboard: every currently
 * active event's reason and removal deadline, and a link to the
 * facility-scoped episodic events ledger. Plural on purpose — an overlapping
 * PLANNED and UNPLANNED event are both legal at once, and hiding one would
 * misstate the facility's obligations.
 */
export const WasteEpisodicEventsTile = () => {
  const { tenantId } = useTenant();
  const { effectiveWasteFacilityId } = useWasteFacilityState();

  const { data, loading } = useQuery(FACILITY_EPISODIC_EVENTS_QUERY, {
    variables: {
      facilityId: effectiveWasteFacilityId,
      status: WasteEpisodicEventStatus.Active,
      // One planned + one unplanned can overlap; anything past 2 would be a
      // data bug, but the list renders whatever comes back.
      pageSize: 5,
    },
    skip: !effectiveWasteFacilityId,
  });
  const activeEvents = data?.facility?.episodicEvents?.items ?? [];

  return (
    <DashboardTile size={{ sm: 12, md: 6 }}>
      <DashboardTile.Header title="Episodic events" />
      <DashboardTile.ContentArea>
        <Stack spacing={2} alignItems="flex-start">
          {loading && !data ? (
            <Skeleton variant="text" width={220} height={48} />
          ) : activeEvents.length > 0 ? (
            <Stack spacing={1.5}>
              {activeEvents.map((event) => (
                <Stack key={event.id}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {event.reason}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Removal deadline {formatContainerDate(event.accumulationDeadline)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No active episodic event
            </Typography>
          )}
          <Button
            component={RouterLink}
            to={`/o/${tenantId}/waste/episodic-events?facilityId=${effectiveWasteFacilityId}`}
            variant="text"
            size="small"
            endIcon={<ArrowForward />}
          >
            View episodic events
          </Button>
        </Stack>
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
};
